import React from 'react';
import { Box, Typography } from '@mui/material';

interface KeywordData {
  keyword: string;
  count: number;
  percentage: number;
}

interface TopKeywordsProps {
  data: KeywordData[];
}

const TopKeywords: React.FC<TopKeywordsProps> = ({ data }) => {
  const COLORS = ['#9B6DFF', '#FF4B8C', '#4caf50', '#ffb74d', '#68f1d1'];
  const RADIUS = 90;
  const STROKE_WIDTH = 15;
  const CIRCLE_GAP = 4; // Espace entre les cercles

  // Calculer le total pour les pourcentages
  const totalCount = data.reduce((sum, item) => sum + item.count, 0);

  // Prendre les 5 premiers mots-clés
  const topKeywords = data.slice(0, 5);

  // Trouver le nombre maximum d'occurrences
  const maxCount = Math.max(...topKeywords.map(item => item.count));

  return (
    <Box sx={{ 
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 2
    }}>
      {/* SVG pour les cercles - centré horizontalement */}
      <Box sx={{ 
        width: '220px', 
        height: '220px',
        display: 'flex',
        justifyContent: 'center'
      }}>
        <svg width="220" height="220" viewBox="0 0 200 200">
          <g transform="translate(100, 100)">
            {/* Cercle grisé de base */}
            <circle
              r={RADIUS}
              fill="none"
              stroke="rgba(255, 255, 255, 0.1)"
              strokeWidth={STROKE_WIDTH}
              transform="rotate(-90)"
            />

            {/* Cercles colorés */}
            {topKeywords.map((keyword, index) => {
              const radius = RADIUS - (index * (STROKE_WIDTH + CIRCLE_GAP));
              const circumference = 2 * Math.PI * radius;
              // Calculer le pourcentage par rapport au maximum (80% max)
              const percentage = (keyword.count / maxCount) * 80;
              const strokeDasharray = circumference;
              const strokeDashoffset = circumference * (1 - percentage / 100);
              
              return (
                <circle
                  key={index}
                  r={radius}
                  fill="none"
                  stroke={COLORS[index]}
                  strokeWidth={STROKE_WIDTH}
                  strokeDasharray={strokeDasharray}
                  strokeDashoffset={strokeDashoffset}
                  transform="rotate(-90)"
                />
              );
            })}
          </g>
        </svg>
      </Box>

      {/* Légende en dessous, sur plusieurs lignes si nécessaire */}
      <Box sx={{ 
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: 2,
        px: 2
      }}>
        {topKeywords.map((keyword, index) => (
          <Typography
            key={index}
            sx={{
              color: COLORS[index],
              fontSize: '1rem',
              minWidth: 'fit-content',
              textAlign: 'center'
            }}
          >
            {keyword.keyword} {keyword.count}x
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

export default TopKeywords;
